import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import Header from "@/components/header";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";

// Define User interface
interface User {
  id: number;
  email: string;
  [key: string]: any; // Allow other properties
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  github_link: z.string().url("Must be a valid URL"),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateCodePage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const queryClient = useQueryClient();
  useTitle(t("header.create_code") || "Create Code");

  const { data: user, isLoading } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      github_link: "",
    },
  });

  const createCode = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await fetch("/api/codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create code");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/codes"] });
      toast({ title: "Code created successfully" });
      setLocation("/codes");
    },
    onError: (error) => {
      toast({
        title: "Error creating code",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("auth.required")}</CardTitle>
              <CardDescription>{t("auth.sign_in_required")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  const onSubmit = (data: FormData) => {
    createCode.mutate(data);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>{t("other.create_code.title") || "Create Code"}</CardTitle>
            <CardDescription>{t("other.create_code.description") || "Share your code with the community"}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.create_code.form.title") || "Title"}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-zinc-800 border-zinc-700" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.create_code.form.description") || "Description"}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="bg-zinc-800 border-zinc-700 min-h-[120px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="github_link"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.create_code.form.github_link") || "GitHub Link"}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-zinc-800 border-zinc-700" placeholder="https://github.com/username/repo" />
                      </FormControl>
                      <FormDescription>
                        {t("other.create_code.form.github_link_description") || "Link to your GitHub repository"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={createCode.isPending} className="w-full" variant="default">
                  {createCode.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common.submitting") || "Submitting..."}
                    </>
                  ) : (
                    t("common.submit") || "Submit"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
